import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Plus, Trash, ArrowUp, ArrowDown, Flask } from "@phosphor-icons/react";
import { useState } from "react";
import CoshhRegisterPicker from "./CoshhRegisterPicker";

const rid = () => Math.random().toString(36).slice(2, 10);

/** Ordered method-statement steps (title + detail) with move up/down. */
export function RamsMethodSteps({ steps, onChange }) {
  const update = (i, patch) => onChange(steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const move = (i, d) => { const n = [...steps]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  return (
    <div className="space-y-2" data-testid="rams-method-steps">
      {steps.length === 0 && <p className="text-xs text-muted-foreground border border-dashed border-border rounded-md p-4 text-center">No steps yet — describe the works in the order they happen.</p>}
      {steps.map((s, i) => (
        <div key={s.id || i} className="flex gap-2 items-start border border-border rounded-lg p-2.5 bg-card" data-testid={`rams-step-${i}`}>
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-1">{i + 1}</span>
          <div className="flex-1 space-y-1.5">
            <Input value={s.step} onChange={e => update(i, { step: e.target.value })} placeholder="Step title (e.g. Isolate and prove dead)" className="h-8 text-xs font-semibold" data-testid={`rams-step-${i}-title`} />
            <Textarea value={s.detail} onChange={e => update(i, { detail: e.target.value })} placeholder="What is done, by whom, and how it is controlled" rows={2} className="text-xs" data-testid={`rams-step-${i}-detail`} />
          </div>
          <div className="flex flex-col gap-0.5">
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp size={12} /></Button>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => move(i, 1)} disabled={i === steps.length - 1}><ArrowDown size={12} /></Button>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-red-600" onClick={() => onChange(steps.filter((_, idx) => idx !== i))} data-testid={`rams-step-${i}-remove`}><Trash size={12} /></Button>
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...steps, { id: rid(), step: "", detail: "" }])} data-testid="rams-step-add"><Plus size={14} className="mr-1" /> Add step</Button>
    </div>
  );
}

/** Toggleable chip list (PPE items, permit types). */
export function ChipPicker({ options, value, onChange, labelOf = (o) => o, keyOf = (o) => o, testPrefix }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter(v => v !== k) : [...value, k]);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testPrefix}>
      {options.map(o => {
        const k = keyOf(o); const on = value.includes(k);
        return (
          <button key={k} type="button" onClick={() => toggle(k)} data-testid={`${testPrefix}-${k.replace(/\W+/g, "-").toLowerCase()}`}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted text-muted-foreground"}`}>
            {on ? "✓ " : ""}{labelOf(o)}
          </button>
        );
      })}
    </div>
  );
}

/** COSHH rows: substance / hazard / controls. */
export function CoshhTable({ rows, onChange }) {
  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const [pick, setPick] = useState(false);
  const addFromRegister = (subs) => onChange([...rows, ...subs.filter(s => !rows.some(r => r.coshh_id === s.id)).map(s => ({ id: rid(), coshh_id: s.id, substance: s.product, hazard: s.hazard_line, controls: s.controls || `See COSHH assessment ${s.reference}` }))]);
  return (
    <div className="space-y-2" data-testid="rams-coshh">
      {rows.map((r, i) => (
        <div key={r.id || i} className="grid md:grid-cols-[1fr_1fr_1.4fr_auto] gap-2 items-start" data-testid={`rams-coshh-${i}`}>
          <Input value={r.substance} onChange={e => update(i, { substance: e.target.value })} placeholder="Substance" className="h-8 text-xs font-semibold" />
          <Input value={r.hazard} onChange={e => update(i, { hazard: e.target.value })} placeholder="Hazard (irritant, flammable…)" className="h-8 text-xs" />
          <Input value={r.controls} onChange={e => update(i, { controls: e.target.value })} placeholder="Controls (ventilation, gloves, SDS on site…)" className="h-8 text-xs" />
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600" onClick={() => onChange(rows.filter((_, idx) => idx !== i))}><Trash size={14} /></Button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => onChange([...rows, { id: rid(), substance: "", hazard: "", controls: "" }])} data-testid="rams-coshh-add"><Plus size={14} className="mr-1" /> Add substance</Button>
        <Button variant="outline" size="sm" onClick={() => setPick(true)} data-testid="rams-coshh-from-register"><Flask size={14} className="mr-1" /> Add from COSHH register</Button>
      </div>
      <CoshhRegisterPicker open={pick} onOpenChange={setPick} onPick={addFromRegister} />
    </div>
  );
}

/** Personnel rows: name / role / comma-separated qualifications. */
export function OperativesTable({ rows, onChange }) {
  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-2" data-testid="rams-operatives">
      {rows.map((r, i) => (
        <div key={r.user_id || i} className="grid md:grid-cols-[1fr_0.7fr_1.6fr_auto] gap-2 items-start">
          <Input value={r.name} onChange={e => update(i, { name: e.target.value })} placeholder="Name" className="h-8 text-xs font-semibold" />
          <Input value={r.role || ""} onChange={e => update(i, { role: e.target.value })} placeholder="Role" className="h-8 text-xs" />
          <Input value={(r.qualifications || []).join(", ")} onChange={e => update(i, { qualifications: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })} placeholder="Qualifications (comma separated)" className="h-8 text-xs" />
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600" onClick={() => onChange(rows.filter((_, idx) => idx !== i))}><Trash size={14} /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...rows, { user_id: "", name: "", role: "Operative", qualifications: [] }])} data-testid="rams-operative-add"><Plus size={14} className="mr-1" /> Add person</Button>
    </div>
  );
}
